import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import TramiteTypeFormModal from '../components/TramiteTypeFormModal';
import TramiteTypeDeleteModal from '../components/TramiteTypeDeleteModal';
import Pagination from '../components/Pagination';
import './TramiteTypes.css';

const TramiteTypes = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tramiteTypes, setTramiteTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [searching, setSearching] = useState('');
  const [transitos, setTransitos] = useState([]);
  const [filterTransito, setFilterTransito] = useState('todos');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Verificar que sea superadmin
  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchTramiteTypes();
    fetchTransitos();
  }, []);

  const fetchTramiteTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tramite-types');
      setTramiteTypes(response.data);
      setError('');
    } catch (err) {
      setError('Error al cargar tipos de trámites');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransitos = async () => {
    try {
      const response = await axios.get('/api/transitos');
      setTransitos(response.data);
    } catch (err) {
      console.error('Error al cargar tránsitos:', err);
    }
  };

  const handleOpenForm = (typeToEdit = null) => {
    setEditingType(typeToEdit);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setEditingType(null);
    setShowFormModal(false);
  };

  const handleSaveType = async (data) => {
    try {
      if (editingType) {
        await axios.put(`/api/tramite-types/${editingType.id}`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
      } else {
        await axios.post('/api/tramite-types', data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
      }
      fetchTramiteTypes();
      handleCloseForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar tipo de trámite');
    }
  };

  const handleOpenDelete = (typeToDelete) => {
    setTypeToDelete(typeToDelete);
    setShowDeleteModal(true);
  };

  const handleCloseDelete = () => {
    setTypeToDelete(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/tramite-types/${typeToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      fetchTramiteTypes();
      handleCloseDelete();
    } catch (err) {
      setError('Error al eliminar tipo de trámite');
    }
  };

  const getFilteredTypes = () => {
    let filtered = tramiteTypes;

    if (filterTransito !== 'todos') {
      filtered = filtered.filter(t => t.transito_id === parseInt(filterTransito));
    }

    if (searching.trim()) {
      const term = searching.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        t.transito_name?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredTypes = getFilteredTypes();

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTypes.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searching]);

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="📋 Tipos de Trámites" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Gestión de Tipos de Trámites</h2>
              <p className="subtitle">Total: {tramiteTypes.length} tipos registrados</p>
            </div>
            <button onClick={() => handleOpenForm()} className="btn-primary">
              ➕ Nuevo Tipo
            </button>
          </div>

          <div className="filters-bar">
            <div className="filter-group">
              <label>🔍 Buscar:</label>
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={searching}
                onChange={(e) => setSearching(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>🏢 Filtrar por Tránsito:</label>
              <select 
                value={filterTransito} 
                onChange={(e) => setFilterTransito(e.target.value)}
              >
                <option value="todos">Todos los tránsitos</option>
                {transitos.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando tipos de trámites...</p>
          ) : filteredTypes.length === 0 ? (
            <p className="no-data">No hay tipos de trámites registrados</p>
          ) : (
            <div className="table-wrapper">
              <table className="tramite-types-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Tránsito</th>
                    <th>Descripción</th>
                    <th>Tarifa ($)</th>
                    <th>Valor Trámite ($)</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(type => (
                    <tr key={type.id}>
                      <td className="type-name">{type.name}</td>
                      <td className="type-transito">
                        <span className="transito-badge">🏢 {type.transito_name || 'Sin asignar'}</span>
                      </td>
                      <td className="type-description">{type.description || '-'}</td>
                      <td className="type-tarifa">
                        <span className="value-badge" style={{ background: 'var(--secondary-soft)', color: 'var(--text-secondary)' }}>
                          ${type.tarifa?.toLocaleString('es-CO')}
                        </span>
                      </td>
                      <td className="type-value">
                        <span className="value-badge">
                          ${type.value?.toLocaleString('es-CO')}
                        </span>
                      </td>
                      <td className="actions">
                        <button
                          onClick={() => handleOpenForm(type)}
                          className="btn-action btn-edit"
                          title="Editar tipo"
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleOpenDelete(type)}
                          className="btn-action btn-delete"
                          title="Eliminar tipo"
                        >
                          🗑️ Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={filteredTypes.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>

        <TramiteTypeFormModal
          isOpen={showFormModal}
          tramiteType={editingType}
          transitos={transitos}
          onClose={handleCloseForm}
          onSubmit={handleSaveType}
        />

        <TramiteTypeDeleteModal
          isOpen={showDeleteModal}
          tramiteType={typeToDelete}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};

export default TramiteTypes;
